import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAxios from "../../utils/useAxios.js";
import { currentDateAndTime } from "../../utils/generateCurrentDateAndTime.js";
import { showModal, hideModal } from "../../utils/modalUtility.js";
import { useAppContext } from "../../context/AppContext.js";
import { fieldsList } from "../../data/data.js";
import { useAgencies } from "../../hooks/useAgencies.js";
import { useModes } from "../../hooks/useModes.js";
import useMentors from "../../hooks/useMentors.js";
import { INITIAL_FILTERS, USER_DETAILS_INITIAL_STATE } from "./constants.js";
import {
  buildDonorPayload,
  buildDonorQuery,
  downloadFailedRowsExcel,
  mapDonorApiToFormData,
} from "./mappers.js";
import { donorApi } from "./api.js";
import DonorActionButtons from "./components/DonorActionButtons.js";
import ExportDonorModal from "./components/ExportDonorModal.js";
import PasswordChangeModal from "./components/PasswordChangeModal.js";
import ReceiptModal from "./components/ReceiptModal.js";
import UserDetailsModal from "./components/UserDetailsModal.js";
import ExcelUploadModal from "./components/ExcelUploadModal.js";
import BulkUpdateModal from "./components/BulkUpdateModal.js";
import GiftExcelUploadModal from "./components/GiftExcelUploadModal.js";
import DonorListSection from "./components/DonorListSection.js";

function Donor() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [role, setRole] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [receiptDetails, setReceiptDetails] = useState({});
  const [view, setView] = useState("excel");
  const [bulkView, setBulkView] = useState("bulk");
  const [excelFile, setExcelFile] = useState(null);
  const [notFoundMessage, setNotFoundMessage] = useState("");
  const [message, setMessage] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [uploadRows, setUploadRows] = useState([]);
  const [activeTab, setActiveTab] = useState("User Details");
  const [otpLoader, setOtpLoader] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [selectedFields, setSelectedFields] = useState(fieldsList);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [userDetailsFormData, setUserDetailsFormData] = useState(
    USER_DETAILS_INITIAL_STATE,
  );

  const fileInputRef = useRef();
  const bulkFileInputRef = useRef();
  const receiptRef = useRef();
  const searchTimeoutRef = useRef(null);
  const returnUrlRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const axiosAPI = useAxios();
  const { state } = useAppContext();
  const { agencies, loading: agenciesLoading } = useAgencies();
  const { modes, loading: modesLoading } = useModes();
  const { mentors, loading: mentorsLoading } = useMentors();

  const removeFormData = () => {
    setUserDetailsFormData(USER_DETAILS_INITIAL_STATE);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedFields(fieldsList);
    } else {
      setSelectedFields([]);
    }
  };

  const handleCheckboxChange = (field) => {
    setSelectedFields((prev) =>
      prev.includes(field)
        ? prev.filter((item) => item !== field)
        : [...prev, field],
    );
  };

  const handleFilterChange = (field, value) => {
    const updatedFilters = {
      ...filters,
      [field]: value,
    };
    setFilters(updatedFilters);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search typing by 400ms; for dropdown changes or clearing input, search immediately
    const delay = field === "search" && value ? 400 : 0;

    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchDonors(updatedFilters, 1);
    }, delay);
  };

  const fetchDonors = async (customFilters = filters, customPage = page) => {
    setLoading(true);
    setNotFoundMessage("");

    try {
      const queryString = buildDonorQuery(
        customFilters,
        customPage,
        selectedFields,
      );
      const response = await donorApi.getDonors(axiosAPI, queryString);

      if (response.data?.results?.length > 0) {
        setUsers(response.data.results);
        setTotalPages(response.data.totalPages);
      } else {
        setUsers([]);
        setNotFoundMessage("No donors found.");
      }
    } catch (err) {
      setUsers([]);
      if (err.response?.status === 404) {
        setNotFoundMessage("No donors found.");
      } else {
        setNotFoundMessage("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchClick = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setPage(1);
    fetchDonors(filters, 1);
  };

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (
      file &&
      file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      alert("Please upload a valid Excel file (.xlsx)");
      setExcelFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } else {
      setExcelFile(file);
    }
  };

  const handleBulkExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (
      file &&
      file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      alert("Please upload a valid Excel file (.xlsx)");
      setExcelFile(null);
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = null;
      }
    } else {
      setExcelFile(file);
    }
  };

  const handleExcelSubmit = async (e) => {
    e.preventDefault();

    if (!excelFile) {
      alert("Please select an Excel file.");
      return;
    }

    const formData = new FormData();
    formData.append("excel", excelFile);

    try {
      const response = await donorApi.uploadExcel(axiosAPI, formData);

      const {
        message: uploadMessage = "",
        insertedCount = 0,
        rows = [],
        failedExcelBase64,
      } = response.data;

      downloadFailedRowsExcel(failedExcelBase64);
      setUploadRows(rows);

      const failedRows = rows.filter((r) => r.status === "fail");

      if (rows.length === 0) {
        window.notifyMessage(
          "Error",
          "No valid data found in Excel file.",
          "error",
        );
      } else if (insertedCount === 0 && failedRows.length > 0) {
        window.notifyMessage(
          "Upload Failed",
          `No rows were inserted.\nFailed Rows: ${failedRows.length}`,
          "error",
        );
      } else if (failedRows.length > 0) {
        window.notifyMessage(
          "Partial Success",
          `${uploadMessage}\nInserted: ${insertedCount}, Failed: ${failedRows.length}`,
          "warning",
        );
      } else {
        window.notifyMessage(
          "Success",
          "Excel file uploaded successfully!",
          "success",
        );
      }

      fetchDonors();
      if (fileInputRef.current) fileInputRef.current.value = null;
      setExcelFile(null);
      setMessage("");
    } catch (err) {
      console.error("Excel Upload Error:", err.message);
      setUploadRows(err?.response?.data?.rows);
      const errorMsg =
        err?.response?.data?.rows?.[0]?.reason ||
        "Something went wrong while uploading the Excel file.";

      window.notifyMessage("Error", errorMsg, "error");
    }
  };

  const handleBulkExcelSubmit = async (e) => {
    e.preventDefault();

    if (!excelFile) {
      alert("Please select an Excel file.");
      return;
    }

    const formData = new FormData();
    formData.append("excel", excelFile);
    formData.append("upload_date_and_time", currentDateAndTime());

    try {
      const response = await donorApi.uploadBulkExcel(axiosAPI, formData);
      const {
        message: uploadMessage = "",
        insertedCount = 0,
        rows = [],
        failedExcelBase64,
      } = response.data;

      downloadFailedRowsExcel(failedExcelBase64);
      setUploadRows(rows);

      const failedRows = rows.filter((r) => r.status === "fail");

      if (rows.length === 0) {
        window.notifyMessage(
          "Error",
          "No valid data found in Excel file.",
          "error",
        );
      } else if (insertedCount === 0 && failedRows.length > 0) {
        window.notifyMessage(
          "Upload Failed",
          `No rows were inserted.\nFailed Rows: ${failedRows.length}`,
          "error",
        );
      } else if (failedRows.length > 0) {
        window.notifyMessage(
          "Partial Success",
          `${uploadMessage}\nInserted: ${insertedCount}, Failed: ${failedRows.length}`,
          "warning",
        );
      } else {
        window.notifyMessage(
          "Success",
          "Excel file uploaded successfully!",
          "success",
        );
      }

      fetchDonors();
      if (bulkFileInputRef.current) bulkFileInputRef.current.value = null;
      setExcelFile(null);
      setMessage("");
    } catch (err) {
      console.error("Excel Upload Error:", err);
      setUploadRows(err?.response?.data?.rows);
      const errorMsg =
        err?.response?.data?.message ||
        "Something went wrong while uploading the Excel file.";

      window.notifyMessage("Error", errorMsg, "error");
    }
  };

  const handleBulkUpdateModal = () => {
    showModal("newBulkModal");
  };

  const handleResetFilters = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setFilters(INITIAL_FILTERS);
    setPage(1);
    fetchDonors(INITIAL_FILTERS, 1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
      fetchDonors(filters, pageNum);
    }
  };

  const handleUserDetailsChange = (e) => {
    const { id, value } = e.target;
    setUserDetailsFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleMentorSelect = (selectedOption) => {
    setUserDetailsFormData((prev) => ({
      ...prev,
      mentorId: selectedOption ? selectedOption.value : "",
      mentorsName: selectedOption
        ? selectedOption.mentorName || selectedOption.label
        : "",
    }));
  };

  const closeModal = () => {
    setIsEditMode(false);
    removeFormData();
    setCurrentUserId(null);
    setUploadRows([]);
    setActiveTab("User Details");
    hideModal("editModal");

    if (returnUrlRef.current) {
      const targetUrl = returnUrlRef.current;
      returnUrlRef.current = null;
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
      navigate(targetUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // VALIDATION HERE
    const phone = userDetailsFormData.phoneNumber;
    if (!/^\d{10}$/.test(phone)) {
      window.notifyMessage("Error", "Phone number must be 10 digits", "danger");
      return;
    }

    const panNo = userDetailsFormData.panNo;
    if (panNo && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(panNo)) {
      window.notifyMessage("Error", "Invalid PAN number format", "danger");
      return;
    }

    const payload = buildDonorPayload(userDetailsFormData);

    // console.log("Payload to submit:", payload);

    // return; // Remove this line after debugging

    try {
      if (isEditMode) {
        await donorApi.updateDonorById(axiosAPI, currentUserId, payload);
        closeModal();
        hideModal("editModal");
        fetchDonors();
        window.notifyMessage(
          "Success",
          "Donor Details Updated successfully",
          "success",
        );
      } else {
        await donorApi.addDonor(axiosAPI, payload);
        window.notifyMessage("Success", "Donor successfully added", "success");
        closeModal();
        removeFormData();
        hideModal("editModal");
      }

      fetchDonors();
      closeModal();
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        window.notifyMessage("Error", error.response.data.message, "danger");
      } else {
        window.notifyMessage("Error", "There was an error!", "danger");
      }
    }
  };

  const downloadPdf = async () => {
    const input = receiptRef.current;
    if (!input) return;

    const html2canvasModule = await import("html2canvas");
    const jsPDFModule = await import("jspdf");
    const html2canvas = html2canvasModule.default;
    const jsPDF = jsPDFModule.default;

    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`DonationReceipt_${receiptDetails?.full_name || "User"}.pdf`);

    setShowPdfModal(false);
    setReceiptDetails("");
  };

  const fetchReceiptDetails = async (id) => {
    setLoading(true);

    try {
      const response = await donorApi.getReceiptDetails(axiosAPI, id);
      setReceiptDetails(response.data.user_details);
    } catch (error) {
      console.error("Error fetching receipt details:", error);
    } finally {
      setLoading(false);
    }
  };

  const openReceiptModal = (id) => {
    fetchReceiptDetails(id);
    setShowPdfModal(true);
  };

  const downloadExcel = async (tableName) => {
    setDownloading(true);
    const queryString = buildDonorQuery(filters, "1", selectedFields);

    try {
      const response = await donorApi.downloadDonorExcel(
        axiosAPI,
        tableName,
        queryString,
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${tableName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      const errorMessage = error?.response?.statusText || "Unknown error";
      alert("Failed to download file: " + errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  const fetchUserDetails = async (id) => {
    setIsEditMode(true);
    try {
      const response = await donorApi.getDonorById(axiosAPI, id);
      const data = response.data.data;

      setCurrentPhoneNumber(data?.phone_number);
      setUserDetailsFormData(mapDonorApiToFormData(data));
    } catch (error) {
      console.error("Error fetching user details:", error);
      setLoading(false);
    }
  };

  const openOtpModal = (id) => {
    showModal("staticBackdrop");
    setCurrentUserId(id);
  };

  const handlePasswordChange = async () => {
    try {
      if (!newPassword) {
        alert("Please enter a new password");
        return;
      }

      const payload = {
        password: newPassword,
      };

      setOtpLoader(true);
      await donorApi.changePassword(axiosAPI, currentUserId, payload);

      alert("Password changed successfully!");
      setNewPassword("");
      window.notifyMessage(
        "Success",
        "Password changed successfully",
        "success",
      );
    } catch (err) {
      console.error(err);
      alert("Failed to change password.");
    } finally {
      setOtpLoader(false);
    }
  };

  const editModeOn = () => {
    setIsEditable(true);
  };

  const editModeOff = () => {
    setIsEditable(false);
  };

  const openUserDetailsModal = (id) => {
    if (!id) return false;

    setCurrentUserId(id);
    editModeOff();
    showModal("editModal");
    fetchUserDetails(id);

    return true;
  };

  const handleDeleteDonor = async (donorId) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this donor?",
    );
    if (!isConfirmed) return;

    try {
      await donorApi.deleteDonorById(axiosAPI, donorId);
      alert("Donor deleted successfully");
      fetchDonors(filters, page);
    } catch (error) {
      console.error("Error deleting donor:", error);
      alert(error.response?.data?.message || "Failed to delete donor");
    }
  };

  const openAddModal = () => {
    removeFormData();
    showModal("editModal");
  };

  const openUploadDonorModal = () => {
    showModal("newGiftExcelModal");
  };

  const openExportDonorModal = () => {
    showModal("exportDonorExcelModal");
  };

  useEffect(() => {
    if (shouldFetch) {
      fetchDonors();
      setShouldFetch(false);
    }
  }, [shouldFetch]);

  useEffect(() => {
    fetchDonors(INITIAL_FILTERS, 1);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (location.state?.openModal && location.state?.donorId) {
      returnUrlRef.current =
        location.state.returnUrl ||
        (location.state.activeTab === "Recurring donation"
          ? "/recurring/donations"
          : null);

      if (location.state.activeTab) {
        setActiveTab(location.state.activeTab);
      }

      openUserDetailsModal(location.state.donorId);
      navigate(location.pathname, { replace: true });
    }
  }, []);

  useEffect(() => {
    const modalEl = document.getElementById("editModal");
    if (!modalEl) return;

    const handleModalHidden = () => {
      if (returnUrlRef.current) {
        const targetUrl = returnUrlRef.current;
        returnUrlRef.current = null;
        document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("padding-right");
        navigate(targetUrl);
      }
    };

    modalEl.addEventListener("hidden.bs.modal", handleModalHidden);
    return () => {
      modalEl.removeEventListener("hidden.bs.modal", handleModalHidden);
    };
  }, []);

  useEffect(() => {
    const currentRole = state?.user?.role;
    setRole(currentRole);
  }, [state?.user?.role]);

  useEffect(() => {
    if (view === "manual") {
      showModal("editModal");
    }
  }, [view]);

  return (
    <div className="container">
      <div className="page-inner">
        <DonorActionButtons
          downloading={downloading}
          openExportDonorModal={openExportDonorModal}
          openUploadDonorModal={openUploadDonorModal}
          handleBulkUpdateModal={handleBulkUpdateModal}
          openAddModal={openAddModal}
          role={role}
        />

        <ExportDonorModal
          closeModal={closeModal}
          setView={setView}
          selectedFields={selectedFields}
          handleSelectAll={handleSelectAll}
          handleCheckboxChange={handleCheckboxChange}
          filters={filters}
          downloadExcel={downloadExcel}
          downloading={downloading}
        />

        <PasswordChangeModal
          otpLoader={otpLoader}
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          handlePasswordChange={handlePasswordChange}
        />

        <ReceiptModal
          showPdfModal={showPdfModal}
          setShowPdfModal={setShowPdfModal}
          setReceiptDetails={setReceiptDetails}
          receiptRef={receiptRef}
          receiptDetails={receiptDetails}
          downloadPdf={downloadPdf}
        />

        <UserDetailsModal
          isEditMode={isEditMode}
          isEditable={isEditable}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          editModeOn={editModeOn}
          currentUserId={currentUserId}
          currentPhoneNumber={currentPhoneNumber}
          handleSubmit={handleSubmit}
          userDetailsFormData={userDetailsFormData}
          handleUserDetailsChange={handleUserDetailsChange}
          agencies={agencies}
          agenciesLoading={agenciesLoading}
          mentors={mentors}
          mentorsLoading={mentorsLoading}
          handleMentorSelect={handleMentorSelect}
          modes={modes}
          modesLoading={modesLoading}
          closeModal={closeModal}
          role={role}
        />

        <ExcelUploadModal
          modalId="newUserModal"
          title="Add Donor"
          isEditMode={isEditMode}
          closeModal={closeModal}
          setView={setView}
          view={view}
          fileInputRef={fileInputRef}
          handleExcelFileChange={handleExcelFileChange}
          handleExcelSubmit={handleExcelSubmit}
          message={message}
        />

        <BulkUpdateModal
          isEditMode={isEditMode}
          closeModal={closeModal}
          setBulkView={setBulkView}
          bulkView={bulkView}
          bulkFileInputRef={bulkFileInputRef}
          handleBulkExcelFileChange={handleBulkExcelFileChange}
          handleBulkExcelSubmit={handleBulkExcelSubmit}
          message={message}
          uploadRows={uploadRows}
        />

        <GiftExcelUploadModal
          isEditMode={isEditMode}
          closeModal={closeModal}
          setView={setView}
          view={view}
          fileInputRef={fileInputRef}
          handleExcelFileChange={handleExcelFileChange}
          handleExcelSubmit={handleExcelSubmit}
          message={message}
          uploadRows={uploadRows}
        />

        <DonorListSection
          users={users}
          loading={loading}
          notFoundMessage={notFoundMessage}
          role={role}
          filters={filters}
          handleFilterChange={handleFilterChange}
          agencies={agencies}
          agenciesLoading={agenciesLoading}
          mentors={mentors}
          mentorsLoading={mentorsLoading}
          modes={modes}
          modesLoading={modesLoading}
          handleSearchClick={handleSearchClick}
          handleResetFilters={handleResetFilters}
          openReceiptModal={openReceiptModal}
          openUserDetailsModal={openUserDetailsModal}
          openOtpModal={openOtpModal}
          handleDeleteDonor={handleDeleteDonor}
          downloading={downloading}
          page={page}
          totalPages={totalPages}
          handlePageClick={handlePageClick}
        />
      </div>
    </div>
  );
}

export default Donor;
